import { BasemapInk } from './labelLayerStyle'

export type ExportLabelKind = 'workflowPin' | 'location' | 'markup'

export const EXPORT_LABEL_KIND_ORDER: ExportLabelKind[] = [
  'workflowPin',
  'location',
  'markup',
]

export const PT_PER_CSS_PX = 0.75
export const LABEL_FLOOR_PX = 6.5 / PT_PER_CSS_PX
// A Location's own label size can reach 100px; on paper that one label would sprawl across the
// map, so Readable holds every label's own size to 18pt before Overall size scales it.
export const LABEL_CAP_PX = 18 / PT_PER_CSS_PX

// How the live map inks a label, carried over so Readable looks like the map it came from. A
// plated label sits on the light plate in `color` (null: the neutral ink); an unplated one takes
// the ink for the ground it stands on, `tone`, or the basemap's when that was never read.
export interface IExportLabelInk {
  isPlated: boolean
  color: string | null
  tone: BasemapInk | null
}

// A stroke centred on the glyph outline, so half of it paints outside the ink.
export const LABEL_HALO_EM = 0.23

export const KEY_BADGE_RADIUS_PX = 8
export const KEY_BADGE_TEXT_PX = 10
export const KEY_BADGE_WEIGHT = 600

export const CLUSTER_MARKER_RADIUS_PX = 20
export const CLUSTER_MARKER_TEXT_PX = 16
// In ems of the disk's count, so the ring keeps its weight as the disk scales.
export const CLUSTER_RING_EM = 1.5 / CLUSTER_MARKER_TEXT_PX

// Past this many numbered keys a key table stops being read; the rest are counted in its last row.
export const MAX_KEY_ENTRIES = 60
// A PNG's key strip stops widening here; what does not fit is counted in its last row.
export const MAX_KEY_STRIP_COLUMNS = 3

export interface IBox {
  x: number
  y: number
  width: number
  height: number
}

// A rectangle turned clockwise by `angleRad` about its centre on the y-down page.
export interface IOrientedBox {
  centre: { x: number; y: number }
  width: number
  height: number
  angleRad: number
}

// The upright box around an obstacle, and the turned rectangle it bounds when it is one, such as a
// rotated text markup: labels collide with the rectangle, not with the empty corners of its box.
export interface IExportObstacle extends IBox {
  shape?: IOrientedBox
}

// Locations folded on one point: the name that stands for them and what it holds, in words.
export interface IExportLabelStack {
  header: string
  details: string[]
}

export interface IExportLabelFeature {
  id: string
  kind: ExportLabelKind
  anchor: { x: number; y: number }
  icon: IBox | null
  text: string
  sizePx: number
  lineHeightPx: number
  weight: number
  letterSpacingEm: number
  priority: number
  sortKey: number
  pinKey?: string
  pinCount?: number
  markerKey?: string
  ink?: IExportLabelInk
  // A markup's geometry in page px: its label runs along it instead of standing beside a point.
  path?: Array<{ x: number; y: number }>
  isClosed?: boolean
  stack?: IExportLabelStack
  // Locations folded into this marker: its icon wears their count on the upper-left shoulder.
  stackCount?: number
}

// A Location marker with no label of its own: still drawn, so still moved clear of its neighbours.
export interface IExportSceneMarker {
  key: string
  icon: IBox
}

// A Location icon moved clear of a neighbour, tied back to where it really is.
export interface IExportLeader {
  key: string
  from: { x: number; y: number }
  to: { x: number; y: number }
}

// A name the label key can list under a group. `sortKey` orders it as its label would be placed.
export interface IExportKeyMember {
  text: string
  sortKey: number
}

// A workflow pin with no label of its own (a multi-Workflow stack or a shared pin): it still takes
// room and joins clusters. `key` is the pin's coordKey, `count` the Workflows it holds, `members`
// their names (none while Workflow labels are off).
export interface IExportScenePin {
  key: string
  icon: IBox
  count: number
  members: IExportKeyMember[]
}

export interface IExportLabelScene {
  width: number
  height: number
  features: IExportLabelFeature[]
  obstacles: IExportObstacle[]
  pins?: IExportScenePin[]
  markers?: IExportSceneMarker[]
}

export type LabelAlign = 'start' | 'middle' | 'end'

// One line of a label drawn on several, each in its own size and weight, in its own box.
export interface IExportLabelLine {
  text: string
  sizePx: number
  lineHeightPx: number
  weight: number
  box: IBox
  // Read for the ground under this line, which need not be the ground under the first.
  ink?: IExportLabelInk
}

export interface IExportLabelPlacement {
  id: string
  kind: ExportLabelKind
  text: string
  sizePx: number
  lineHeightPx: number
  weight: number
  letterSpacingEm: number
  box: IBox
  align: LabelAlign
  keyTag?: string
  clusterCount?: number
  anchor: { x: number; y: number }
  ink?: IExportLabelInk
  // Set on a label run along a markup: drawn turned by this about `anchor`, its centre. `box` is
  // then the upright box around it, and `runWidthPx` its own unturned width.
  angleRad?: number
  runWidthPx?: number
  // A stack's label on two lines: its name, then what it holds, smaller.
  lines?: IExportLabelLine[]
}

// `details` lists under a numbered stack what its label would have said on the map.
export type IExportKeyEntry =
  | { tag: string; text: string; details?: string[] }
  | { tag: string; count: number; members: string[] }

export type ExportKeyRowKind =
  | 'title'
  | 'single'
  | 'group'
  | 'member'
  | 'overflow'

export interface IExportKeyRow {
  kind: ExportKeyRowKind
  tag: string
  text: string
}

export interface IExportKeyTable {
  box: IBox
  rows: IExportKeyRow[]
  columns: number
  // Each row's column and its top within it: groups open with a gap and never leave their header
  // alone at a column's foot, so rows do not fall on a fixed grid.
  rowColumns: number[]
  rowTops: number[]
  columnWidthPx: number
  rowHeightPx: number
  paddingPx: number
  sizePx: number
  tagColumnPx: number
  tagBoxPx: number
  // The key's scale, for the sizes drawn from it: tag boxes and group disks.
  scale: number
}

export interface IExportLabelResult {
  placements: IExportLabelPlacement[]
  key: IExportKeyEntry[]
  keyTable: IExportKeyTable | null
  hiddenPinKeys: string[]
  // Every Location icon the solver moved, at its new centre in page px.
  movedMarkers: Array<{ key: string; centre: { x: number; y: number } }>
  leaders: IExportLeader[]
  // Every Workflow and Location name on the sheet is on the map, numbered, listed under a group,
  // or counted: key on, names = onMap + numbered + listedMembers + overflow; key off,
  // names = onMap + leftOff + groupNames.
  stats: {
    names: number
    onMap: number
    shrunk: number
    numbered: number
    groups: number
    groupNames: number
    listedMembers: number
    overflow: number
    leftOff: number
    markupShown: number
    markupLeftOff: number
    forcedTags: number
  }
}

export type LabelMeasure = (
  text: string,
  sizePx: number,
  weight: number,
  letterSpacingEm: number,
) => number

export const haloPadPx = (sizePx: number): number =>
  (LABEL_HALO_EM / 2) * sizePx
